import { __rest } from "tslib";
import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
/**
 * @example
 *
 * ```jsx
 * import {
 *   Accordion,
 *   AccordionContent,
 *   AccordionItem,
 *   AccordionTrigger,
 * } from "@skve/components"
 *
 * function Demo() {
 *   return (
 *     <Accordion type="single" collapsible className="w-full">
 *       <AccordionItem value="item-1">
 *         <AccordionTrigger>Is it accessible?</AccordionTrigger>
 *         <AccordionContent>
 *           Yes. It adheres to the WAI-ARIA design pattern.
 *         </AccordionContent>
 *       </AccordionItem>
 *       <AccordionItem value="item-2">
 *         <AccordionTrigger>Is it styled?</AccordionTrigger>
 *         <AccordionContent>
 *           Yes. It comes with default styles that matches the other
 *           components&apos; aesthetic.
 *         </AccordionContent>
 *       </AccordionItem>
 *       <AccordionItem value="item-3">
 *         <AccordionTrigger>Is it animated?</AccordionTrigger>
 *         <AccordionContent>
 *           Yes. It&apos;s animated by default, but you can disable it if you
 *           prefer.
 *         </AccordionContent>
 *       </AccordionItem>
 *     </Accordion>
 *   )
 * }
 * ```
 */
const Accordion = AccordionPrimitive.Root;
const AccordionItem = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(AccordionPrimitive.Item, Object.assign({ ref: ref, className: cn("border-b", className) }, props)));
});
AccordionItem.displayName = "AccordionItem";
const AccordionTrigger = React.forwardRef((_a, ref) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    return (React.createElement(AccordionPrimitive.Header, { className: "flex" },
        React.createElement(AccordionPrimitive.Trigger, Object.assign({ ref: ref, className: cn("flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180", className) }, props),
            children,
            React.createElement(ChevronDown, { className: "h-4 w-4 shrink-0 transition-transform duration-200" }))));
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
const AccordionContent = React.forwardRef((_a, ref) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    return (React.createElement(AccordionPrimitive.Content, Object.assign({ ref: ref, className: "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down" }, props),
        React.createElement("div", { className: cn("pb-4 pt-0", className) }, children)));
});
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
//# sourceMappingURL=accordion.js.map